import { shortUuid } from '@tunasong/models'

/**
 * Deterministic entity id for an auto-created advisor agent, derived from
 * `(parentId, definitionId)`.
 *
 * Why this exists (replay-idempotency): `resolveOrAttachAgent`'s
 * `createEntity` call is queued for offline replay by
 * `mutationQueueMiddleware` (lib/redux/src/api/mutation-queue-middleware.ts)
 * whenever it fails with a retriable status (e.g. a 500 during deploy-skew).
 * The queue replays the *original* create args verbatim on reconnect —
 * bypassing `resolveOrAttachAgent`'s find-or-create check entirely — so a
 * server-assigned id would mean every replay (one per failed visit) mints a
 * brand new, distinct advisor for the same song.
 *
 * A stable id keyed off the pair means every create attempt — the first live
 * one and any later replay — targets the same row. `DynamoEntityDB.create`
 * uses a conditional put (`ConditionExpression: 'attribute_not_exists(id)'`),
 * so only the first attempt actually creates the row; every other attempt
 * fails with `ConditionalCheckFailedException`. `classifyReplayResult` in
 * `lib/redux/src/api/mutation-queue.ts` already special-cases that exact
 * failure for `createEntity` replays as `'success'` (entity already exists —
 * desired state reached), so the queue drops the entry instead of retrying
 * or duplicating. No code change was needed there; this id is the missing
 * piece that makes that existing handling reachable for advisor auto-create.
 *
 * The id must satisfy `isShortUuid` (schemas/src/id/short-id.ts) since
 * `DynamoEntityDB.create` validates client-supplied ids against it. SHA-256
 * (rather than a cheap 32-bit string hash) keeps collisions across unrelated
 * `(parentId, definitionId)` pairs cryptographically negligible — a
 * collision here would attach the wrong advisor to a song.
 */
export const deterministicAgentId = async (parentId: string, definitionId: string): Promise<string> => {
  const seed = `${parentId}:${definitionId}`
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(seed))
  const hex = Array.from(new Uint8Array(digest))
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('')
  // shortUuid's `uuid` param only needs to be hex — it doesn't have to be a
  // real UUID. 16 bytes (128 bits) of the digest is plenty of entropy and
  // matches the width shortUuid already expects from real UUIDs.
  return shortUuid({ uuid: hex.slice(0, 32) })
}
